import {
  EdgelessLegacySlotIdentifier,
  OverlayIdentifier,
} from '@blocksuite/affine-block-surface';
import {
  type ConnectorElementModel,
  LocalConnectorElementModel,
} from '@blocksuite/affine-model';
import { DisposableGroup } from '@blocksuite/global/disposable';
import { Vec } from '@blocksuite/global/gfx';
import { WithDisposable } from '@blocksuite/global/lit';
import {
  type BlockComponent,
  type BlockStdScope,
  stdContext,
  storeContext,
} from '@blocksuite/std';
import { GfxControllerIdentifier, type GfxModel } from '@blocksuite/std/gfx';
import type { Store } from '@blocksuite/store';
import { consume } from '@lit/context';
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  type ConnectionOverlay,
  ConnectorPathGenerator,
} from '../connector-manager';
import { ConnectorReconnectSession } from '../reconnect-session';

const SIZE = 12;
const HALF_SIZE = SIZE / 2;

export class EdgelessConnectorHandle extends WithDisposable(LitElement) {
  static override styles = css`
    .line-controller {
      position: absolute;
      width: ${SIZE}px;
      height: ${SIZE}px;
      box-sizing: border-box;
      border-radius: 50%;
      border: 2px solid var(--streamlain-text-emphasis-color);
      background-color: var(--streamlain-background-primary-color);
      cursor: pointer;
      z-index: 10;
      pointer-events: all;
      /**
       * Fix: pointerEvent stops firing after a short time.
       * When a gesture is started, the browser intersects the touch-action values of the touched element and its ancestors,
       * up to the one that implements the gesture (in other words, the first containing scrolling element)
       * https://developer.mozilla.org/en-US/docs/Web/CSS/touch-action
       */
      touch-action: none;
    }
    .line-controller-hidden {
      display: none;
    }
  `;

  private _lastZoom = 1;

  private _reconnectDisposables: DisposableGroup | null = null;

  get connectionOverlay() {
    return this.std.get(OverlayIdentifier('connection')) as ConnectionOverlay;
  }

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get slots() {
    return this.std.get(EdgelessLegacySlotIdentifier);
  }

  private _bindEvent() {
    const slots = this.slots;

    this._disposables.addFromEvent(this._startHandler, 'pointerdown', e => {
      slots.elementResizeStart.next();
      this._capPointerDown(e, 'source');
    });
    this._disposables.addFromEvent(this._endHandler, 'pointerdown', e => {
      slots.elementResizeStart.next();
      this._capPointerDown(e, 'target');
    });
    this._disposables.add(() => {
      this.connectionOverlay.clear();
    });
  }

  private _capPointerDown(e: PointerEvent, connection: 'target' | 'source') {
    const { gfx, connector, slots } = this;
    e.stopPropagation();
    const pointerId = e.pointerId;
    const captureTarget = e.currentTarget as HTMLDivElement;
    const session = new ConnectorReconnectSession(connector[connection]);
    const preview = this._createPreviewConnector();
    const reconnectDisposables = new DisposableGroup();
    this._reconnectDisposables?.dispose();
    this._reconnectDisposables = reconnectDisposables;

    const finish = (commit: boolean) => {
      if (commit) {
        if (
          !session.commit(
            () => this.doc.captureSync(),
            value => {
              connector[connection] = value;
              ConnectorPathGenerator.updatePath(connector, null, id =>
                gfx.getElementById<GfxModel>(id)
              );
            }
          )
        ) {
          return;
        }
      } else if (session.cancel()) {
        ConnectorPathGenerator.updatePath(
          connector,
          null,
          id => gfx.getElementById<GfxModel>(id),
          { updateLabel: false }
        );
      } else {
        return;
      }

      reconnectDisposables.dispose();
      if (captureTarget.hasPointerCapture(pointerId)) {
        captureTarget.releasePointerCapture(pointerId);
      }
      if (this._reconnectDisposables === reconnectDisposables) {
        this._reconnectDisposables = null;
      }
      this.connectionOverlay.clear();
      this.requestUpdate();
      slots.elementResizeEnd.next();
    };

    reconnectDisposables.addFromEvent(document, 'pointermove', event => {
      if (event.pointerId !== pointerId) return;
      const point = gfx.viewport.toModelCoordFromClientCoord([
        event.x,
        event.y,
      ]);
      const isStartPointer = connection === 'source';
      const otherSideId = connector[isStartPointer ? 'target' : 'source'].id;

      const pending = this.connectionOverlay.renderConnector(
        point,
        [connector.id, ...(otherSideId ? [otherSideId] : [])],
        session.activeTargetId
      );
      session.update(pending);
      preview[connection] = structuredClone(pending);
      ConnectorPathGenerator.updatePath(
        preview,
        null,
        id => gfx.getElementById<GfxModel>(id),
        { updateLabel: false }
      );
      connector.xywh = preview.xywh;
      connector.path = preview.path.map(point => point.clone());
      this.requestUpdate();
    });

    reconnectDisposables.addFromEvent(document, 'pointerup', event => {
      if (event.pointerId !== pointerId) return;
      finish(true);
    });
    reconnectDisposables.addFromEvent(document, 'pointercancel', event => {
      if (event.pointerId !== pointerId) return;
      finish(false);
    });
    reconnectDisposables.addFromEvent(captureTarget, 'lostpointercapture', () =>
      finish(false)
    );
    reconnectDisposables.addFromEvent(document, 'keydown', event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      finish(false);
    });

    captureTarget.setPointerCapture(pointerId);
  }

  private _createPreviewConnector() {
    const { connector } = this;
    const preview = new LocalConnectorElementModel(connector.surface);
    preview.id = connector.id;
    preview.xywh = connector.xywh;
    preview.source = structuredClone(connector.source);
    preview.target = structuredClone(connector.target);
    preview.mode = connector.mode;
    preview.stroke = connector.stroke;
    preview.strokeStyle = connector.strokeStyle;
    preview.strokeWidth = connector.strokeWidth;
    preview.rough = connector.rough;
    preview.roughness = connector.roughness;
    preview.frontEndpointStyle = connector.frontEndpointStyle;
    preview.rearEndpointStyle = connector.rearEndpointStyle;
    return preview;
  }

  override firstUpdated() {
    const { gfx } = this;
    const { viewport } = gfx;

    this._lastZoom = viewport.zoom;
    viewport.viewportUpdated.subscribe(() => {
      if (viewport.zoom !== this._lastZoom) {
        this._lastZoom = viewport.zoom;
        this.requestUpdate();
      }
    });

    this._bindEvent();
    this._disposables.add(() => this._reconnectDisposables?.dispose());
  }

  override render() {
    const { gfx } = this;
    // path is relative to the element's xywh
    const { path } = this.connector;
    if (!path || path.length === 0) return html``;
    const zoom = gfx.viewport.zoom;
    const startPoint = Vec.subScalar(Vec.mul(path[0], zoom), HALF_SIZE);
    const endPoint = Vec.subScalar(
      Vec.mul(path[path.length - 1], zoom),
      HALF_SIZE
    );
    const startStyle = {
      transform: `translate3d(${startPoint[0]}px,${startPoint[1]}px,0)`,
    };
    const endStyle = {
      transform: `translate3d(${endPoint[0]}px,${endPoint[1]}px,0)`,
    };
    return html`
      <div
        class="line-controller line-start"
        style=${styleMap(startStyle)}
      ></div>
      <div class="line-controller line-end" style=${styleMap(endStyle)}></div>
    `;
  }

  @query('.line-end')
  private accessor _endHandler!: HTMLDivElement;

  @query('.line-start')
  private accessor _startHandler!: HTMLDivElement;

  @property({ attribute: false })
  accessor connector!: ConnectorElementModel;

  @consume({
    context: storeContext,
  })
  accessor doc!: Store;

  @property({ attribute: false })
  accessor edgeless!: BlockComponent;

  @consume({
    context: stdContext,
  })
  accessor std!: BlockStdScope;
}
